// src/utils/markerHistory.js
//
// Builds a per-marker draw history from a patient's triage reports — each
// report's claudeRawResponse carries a `markers` array (name/value/status/
// unit) for that single draw; this groups those across every report into
// one series per marker name, oldest first, so a marker's trend can be
// plotted as a sparkline. Used by the Current Markers tab (click a marker
// to see its trend) — previously local to the Health Story page's now-
// removed "Marker trends" section, moved here so both pages can build the
// same shape from the same triage-history response.
//
// Matching a marker card (effective-panel's canonical markerName) back to
// this map's keys (whatever name the LLM extracted per draw) is necessarily
// best-effort — see matchMarkerHistory below.

export function buildMarkerHistoryMap(reports) {
  const markerMap = {};
  if (!reports?.length) return markerMap;

  const sorted = [...reports].sort((a, b) =>
    new Date(a.drawDate || a.createdAt) - new Date(b.drawDate || b.createdAt));

  sorted.forEach(r => {
    const date = r.drawDate || r.createdAt;
    try {
      const raw = typeof r.claudeRawResponse === 'string'
        ? JSON.parse(r.claudeRawResponse) : r.claudeRawResponse;
      (raw?.markers || []).forEach(m => {
        const name = m.name;
        const val  = parseFloat(m.value);
        if (!name || isNaN(val)) return;
        if (!markerMap[name]) markerMap[name] = [];
        markerMap[name].push({ date, val, status: m.status, unit: m.unit });
      });
    } catch { /* skip malformed */ }
  });

  return markerMap;
}

// A triage report's per-draw marker name (LLM-extracted, e.g. "ALT (SGPT)")
// doesn't always match the effective panel's canonical markerName (e.g.
// "SGPT") exactly, so this tries an exact case-insensitive match first, then
// falls back to either name containing the other — good enough to find the
// right series without a backend canonical-code link on marker_observations.
export function matchMarkerHistory(markerMap, markerName, canonicalCode) {
  if (!markerMap) return null;
  const candidates = [markerName, canonicalCode].filter(Boolean);
  for (const candidate of candidates) {
    const needle = candidate.trim().toLowerCase();
    const exact = Object.keys(markerMap).find(key => key.trim().toLowerCase() === needle);
    if (exact) return markerMap[exact];
  }
  for (const candidate of candidates) {
    const needle = candidate.trim().toLowerCase();
    const partial = Object.keys(markerMap).find(key => {
      const k = key.trim().toLowerCase();
      return k.includes(needle) || needle.includes(k);
    });
    if (partial) return markerMap[partial];
  }
  return null;
}
